/**
 * First-run deferral marker (T30.2).
 *
 * A located household never re-enters first run (the gate keys on location
 * absence). But a manager can SKIP the location step — first run must never
 * block app use (AC3). Without a marker, the gate would see "manager + still no
 * location" on the very next navigation and bounce them straight back into the
 * flow, trapping them. So when the manager skips or finishes, we record a
 * household-scoped deferral in localStorage; the gate honours it and lets them
 * into the app. They can still resume setup any time from Store preferences /
 * the Home-location settings card.
 *
 * Scoped per household id so switching to a different unconfigured household
 * still surfaces first run for that one. Device-local only — no backend channel,
 * no schema (T30.2 Boundaries: no schema). A located household doesn't depend on
 * this marker at all; it's purely the "I chose to defer" escape hatch.
 */

const KEY_PREFIX = 'shopista.onboarding.deferred.';

function key(householdId: string): string {
  return `${KEY_PREFIX}${householdId}`;
}

/** Record that the manager skipped/finished first run for this household. */
export function markFirstRunDeferred(householdId: string): void {
  if (!householdId) return;
  try {
    window.localStorage.setItem(key(householdId), '1');
  } catch {
    // localStorage disabled (private mode / blocked) — the worst case is the
    // gate re-offers first run, never a crash. Fail soft.
  }
}

/** True when the manager has already skipped/finished first run for this household. */
export function isFirstRunDeferred(householdId: string | undefined): boolean {
  if (!householdId) return false;
  try {
    return window.localStorage.getItem(key(householdId)) === '1';
  } catch {
    return false;
  }
}
